import { useState, useCallback, useRef, useEffect } from 'react';
import { track, ANALYTICS_EVENTS } from '../utils/productAnalytics';
// @ts-ignore - Remotion type compatibility
import type { PlayerRef } from '@remotion/player';

interface UseRemotionExportParams {
  playerRef: React.RefObject<PlayerRef>;
  containerRef: React.RefObject<HTMLDivElement>;
  durationInFrames: number;
  fps: number;
  width: number;
  height: number;
}

interface CanvasCaptureTrack extends MediaStreamTrack {
  requestFrame(): void;
}

interface UseRemotionExportResult {
  startExport: () => void;
  progress: number;
  isExporting: boolean;
  error: string | null;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function getSupportedMimeType(): string {
  const types = [
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/webm',
  ];
  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) return type;
  }
  return 'video/webm';
}

function getImageFormat(): { type: string; quality: number } {
  const testCanvas = document.createElement('canvas');
  testCanvas.width = 1;
  testCanvas.height = 1;
  if (testCanvas.toDataURL('image/webp').startsWith('data:image/webp')) {
    return { type: 'image/webp', quality: 0.9 };
  }
  return { type: 'image/jpeg', quality: 0.92 };
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Failed to create blob'))),
      type,
      quality,
    );
  });
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function findCompositionElement(
  container: HTMLElement,
  targetWidth: number,
  targetHeight: number,
): HTMLElement | null {
  // Remotion Player renders composition at full resolution (e.g. 1080x1920)
  // then scales it down via CSS transform to fit the container.
  // Find the inner element with the exact composition dimensions.
  const divs = container.querySelectorAll('div');
  for (let i = 0; i < divs.length; i++) {
    const div = divs[i];
    const w = div.offsetWidth;
    const h = div.offsetHeight;
    if (w === targetWidth && h === targetHeight) {
      return div as HTMLElement;
    }
  }
  return null;
}

const IMPORTANT_STYLE_PROPS = [
  'position', 'display', 'flex-direction', 'justify-content', 'align-items',
  'width', 'height', 'min-width', 'min-height', 'max-width', 'max-height',
  'top', 'left', 'right', 'bottom',
  'margin', 'padding', 'border', 'border-radius',
  'background', 'background-color', 'background-image',
  'color', 'font-family', 'font-size', 'font-weight', 'line-height',
  'letter-spacing', 'text-align', 'text-shadow', 'text-decoration',
  'opacity', 'transform', 'transform-origin',
  'overflow', 'box-shadow', 'gap', 'flex-shrink', 'flex-grow',
  'white-space', 'word-break',
];

function inlineStyles(source: HTMLElement, target: HTMLElement): void {
  const computed = window.getComputedStyle(source);
  for (const prop of IMPORTANT_STYLE_PROPS) {
    const value = computed.getPropertyValue(prop);
    if (value) {
      target.style.setProperty(prop, value);
    }
  }
}

function deepInlineStyles(source: HTMLElement, target: HTMLElement): void {
  inlineStyles(source, target);
  const sourceChildren = source.children;
  const targetChildren = target.children;
  for (let i = 0; i < sourceChildren.length && i < targetChildren.length; i++) {
    deepInlineStyles(
      sourceChildren[i] as HTMLElement,
      targetChildren[i] as HTMLElement,
    );
  }
}

async function captureElementToCanvas(
  element: HTMLElement,
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
): Promise<void> {
  const clone = element.cloneNode(true) as HTMLElement;
  deepInlineStyles(element, clone);

  clone.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
  clone.style.width = `${width}px`;
  clone.style.height = `${height}px`;
  // Remove any scaling transform applied by Remotion Player
  clone.style.transform = 'none';

  const serialized = new XMLSerializer().serializeToString(clone);

  const svgString = [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">`,
    `<foreignObject width="100%" height="100%">`,
    serialized,
    `</foreignObject>`,
    `</svg>`,
  ].join('');

  const svgBlob = new Blob([svgString], {
    type: 'image/svg+xml;charset=utf-8',
  });
  const url = URL.createObjectURL(svgBlob);

  try {
    await new Promise<void>((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        resolve();
      };
      img.onerror = () => reject(new Error('Failed to render frame to canvas'));
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function useRemotionExport({
  playerRef,
  containerRef,
  durationInFrames,
  fps,
  width,
  height,
}: UseRemotionExportParams): UseRemotionExportResult {
  const [progress, setProgress] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  const startExport = useCallback(async () => {
    const player = playerRef.current;
    const container = containerRef.current;

    if (!player || !container) {
      setError('プレーヤーの準備ができていません');
      return;
    }

    if (!('MediaRecorder' in window)) {
      setError('お使いのブラウザは動画エクスポートに対応していません');
      return;
    }

    const testCanvas = document.createElement('canvas');
    if (typeof testCanvas.captureStream !== 'function') {
      setError('お使いのブラウザは動画エクスポートに対応していません。Chrome または Firefox をお使いください。');
      return;
    }

    cancelledRef.current = false;
    setIsExporting(true);
    setProgress(0);
    setError(null);

    try {
      player.pause();

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas 2D context is not available');

      // Find the inner composition element rendered by Remotion Player
      const compositionEl =
        findCompositionElement(container, width, height) || container;

      const imageFormat = getImageFormat();

      // Phase 1: Capture all frames as compressed image blobs (0-80%)
      const frameBlobs: Blob[] = [];
      for (let frame = 0; frame < durationInFrames; frame++) {
        if (cancelledRef.current) throw new Error('cancelled');

        player.seekTo(frame);
        await sleep(40);
        await captureElementToCanvas(compositionEl, canvas, ctx, width, height);

        const blob = await canvasToBlob(canvas, imageFormat.type, imageFormat.quality);
        frameBlobs.push(blob);

        setProgress(Math.round((frame / durationInFrames) * 80));
      }

      if (cancelledRef.current) throw new Error('cancelled');

      // Phase 2: Encode captured frames to video with correct timing (80-100%)
      const mimeType = getSupportedMimeType();
      const stream = canvas.captureStream(0);
      const videoTrack = stream.getVideoTracks()[0];

      const recorder = new MediaRecorder(stream, {
        mimeType,
        videoBitsPerSecond: 8_000_000,
      });

      const chunks: Blob[] = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      const recordingDone = new Promise<void>((resolve) => {
        recorder.onstop = () => resolve();
      });

      recorder.start();

      const frameDuration = 1000 / fps;
      let nextFrameTime = performance.now();

      for (let i = 0; i < frameBlobs.length; i++) {
        if (cancelledRef.current) {
          recorder.stop();
          throw new Error('cancelled');
        }

        const bitmap = await createImageBitmap(frameBlobs[i]);
        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(bitmap, 0, 0, width, height);
        bitmap.close();

        // Push the frame to the video stream
        if ('requestFrame' in videoTrack) {
          (videoTrack as CanvasCaptureTrack).requestFrame();
        } else {
          await sleep(16);
        }

        // Maintain correct frame timing for accurate video duration
        nextFrameTime += frameDuration;
        const waitTime = nextFrameTime - performance.now();
        if (waitTime > 0) {
          await sleep(waitTime);
        }

        setProgress(80 + Math.round((i / frameBlobs.length) * 20));
      }

      recorder.stop();
      await recordingDone;

      // Release frame blobs to free memory
      frameBlobs.length = 0;

      // Trigger download
      const videoBlob = new Blob(chunks, { type: mimeType });
      downloadBlob(videoBlob, `template-video-${Date.now()}.webm`);
      track(ANALYTICS_EVENTS.DOWNLOAD_COMPLETED, { type: 'movie', format: 'webm' });

      setProgress(100);
    } catch (err) {
      if (err instanceof Error && err.message === 'cancelled') {
        setProgress(0);
      } else {
        console.error('Export error:', err);
        setError('エクスポートに失敗しました。ページを再読み込みしてもう一度お試しください。');
      }
    } finally {
      setIsExporting(false);
    }
  }, [playerRef, containerRef, durationInFrames, fps, width, height]);

  return { startExport, progress, isExporting, error };
}
